"use client";

import { type ClassValue, clsx } from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import type { ComponentType } from "react";
import { useState } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type NavItem = {
  id: string;
  icon: ComponentType;
  label: string;
  color: string;
  labelClassName?: string;
};

export type NavbarNavProps = {
  items: NavItem[];
  activeItemId?: string;
  onItemClick?: (id: string) => void;
  className?: string;
  labelClassName?: string;
};

const EXPANDED_WIDTH = 140;
const COLLAPSED_WIDTH = 50;

export const NavbarNav = ({
  items,
  activeItemId,
  onItemClick,
  className,
  labelClassName = "ml-3 font-medium text-white max-sm:hidden",
}: NavbarNavProps) => {
  const [internalActiveId, setInternalActiveId] = useState(
    activeItemId || items[0]?.id
  );

  // Use controlled activeItemId if provided, otherwise use internal state
  const currentActiveId = activeItemId || internalActiveId;

  const handleItemClick = (id: string) => {
    if (onItemClick) {
      onItemClick(id);
    } else {
      setInternalActiveId(id);
    }
  };

  return (
    <div className={cn("flex h-full items-center bg-transparent", className)}>
      {items.map((item) => {
        const isActive = currentActiveId === item.id;
        const Icon = item.icon;

        return (
          <motion.div
            animate={{
              width: isActive ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
            }}
            className={cn(
              "flex h-full cursor-pointer items-center justify-center overflow-hidden",
              item.color,
              isActive ? "flex-1" : "flex-none"
            )}
            initial={false}
            key={item.id}
            layout
            onClick={() => handleItemClick(item.id)}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30,
            }}
          >
            <motion.div
              animate={{ filter: "blur(0px)" }}
              className="flex h-full items-center justify-center"
              exit={{ filter: "blur(10px)" }}
              initial={{ filter: "blur(10px)" }}
              transition={{ duration: 0.25, ease: "easeOut" }}
            >
              <Icon />
              <AnimatePresence initial={false}>
                {isActive && (
                  <motion.span
                    animate={{ opacity: 1, scaleX: 1 }}
                    className={item.labelClassName || labelClassName}
                    exit={{ opacity: 0, scaleX: 0.8 }}
                    initial={{ opacity: 0, scaleX: 0.8 }}
                    style={{ originX: 0 }}
                    transition={{ duration: 0.25, ease: "easeOut" }}
                  >
                    {item.label}
                  </motion.span>
                )}
              </AnimatePresence>
            </motion.div>
          </motion.div>
        );
      })}
    </div>
  );
};
